/**
 * Milestone-1 analytics: an in-memory + localStorage ring buffer of events,
 * mirrored to the console in dev. The MVP swaps `sink` for a POST to the API.
 * Event names are the contract the dashboards will be built on.
 */
import { readJson, writeJson } from './storage';

export type TalesEvent =
  | { type: 'session_start'; series: string; episode: string; referrer: string; query: string; inApp: boolean }
  | { type: 'play_start'; series: string; episode: string; node: string }
  | { type: 'node_start'; node: string; gapMs?: number }
  | { type: 'node_complete'; node: string; watchedMs: number }
  | { type: 'choice_shown'; node: string; options: string[]; timer?: number }
  | { type: 'choice_made'; node: string; choice: string; decisionMs: number; timedOut: boolean }
  | { type: 'ending_reached'; node: string; ending: string; pathLength: number; totalMs: number }
  | { type: 'replay'; from: string }
  | { type: 'share'; method: string }
  | { type: 'unlock_gate_shown'; series: string; episode: string; price: number }
  | { type: 'buy_sheet_shown'; series: string; episode: string; context: string }
  | { type: 'purchase_intent'; series: string; episode: string; sku: string }
  | { type: 'unlock'; series: string; episode: string; price: number }
  | { type: 'error'; where: string; message: string };

type Stored = TalesEvent & { t: number; sid: string };

const KEY = 'tales.events.v1';
const MAX = 500;
const sid = Math.random().toString(36).slice(2, 10);
const buffer: Stored[] = readJson<Stored[]>(KEY, []);
let sink: ((e: Stored) => void) | undefined;
let context: { series?: string; episode?: string } = {};

export function setSink(fn: (e: Stored) => void): void {
  sink = fn;
}

/** Series/episode stamped onto every event so server-side aggregation never has to guess the context. */
export function setContext(ctx: { series?: string; episode?: string }): void {
  context = ctx;
}

export function track(e: TalesEvent): void {
  const stored: Stored = { ...context, ...e, t: Date.now(), sid };
  buffer.push(stored);
  if (buffer.length > MAX) buffer.splice(0, buffer.length - MAX);
  writeJson(KEY, buffer);
  if (import.meta.env.DEV) console.debug('[tales]', e.type, e);
  sink?.(stored);
}

export function events(): readonly Stored[] {
  return buffer;
}

declare global {
  interface Window {
    __tales?: { events: () => readonly Stored[]; clear: () => void };
  }
}
if (typeof window !== 'undefined') {
  window.__tales = {
    events,
    clear: () => {
      buffer.length = 0;
      writeJson(KEY, []);
    },
  };
}
